import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { defineToolSpec } from '../tooling/types';
import type { CopilotToolSpec } from '../tooling/types';
import type { AgentTweakableToolCall, AgentTweakableToolDefinition } from './AgentTweakableState';
import { AGENT_TWEAKABLE_TOOL_NAMES } from './AgentTweakableState';
import type { AgentTweakableToolName } from './AgentTweakableState';
import { useAgentTweakableTools } from './react';

export type PromptFragmentInfo = {
  text: string;
  lineCount: number;
  keys: string[];
  keySample: string[];
};

type AgentTweakableDeps = {
  getFrontendTools: () => AgentTweakableToolDefinition[];
  tryHandleToolCall: (
    name: string,
    toolCall: AgentTweakableToolCall
  ) => { handled: boolean; result?: unknown; errorText?: string };
};

const TOOL_VALUE_SCHEMAS: Record<AgentTweakableToolName, z.ZodType> = {
  updateTextField: z.string().min(1, 'value must be a non-empty string'),
  updateCheckbox: z.boolean({ message: 'value must be a boolean (true or false)' }),
  updateSelect: z.string().trim().min(1, 'value must be a non-empty string'),
  clickButton: z.boolean({ message: 'value must be a boolean (true to click)' }),
};

function buildInputSchema(toolName: AgentTweakableToolName) {
  return z.object({
    key: z.string({ message: 'key is required' }).min(1, 'key must be a non-empty string'),
    value: TOOL_VALUE_SCHEMAS[toolName],
  });
}

function makeTweakableToolSpec(
  toolName: AgentTweakableToolName,
  deps: AgentTweakableDeps
): CopilotToolSpec {
  return defineToolSpec({
    name: toolName,
    inputSchema: buildInputSchema(toolName),
    getDefinition: () => deps.getFrontendTools().find((t) => t.name === toolName) ?? null,
    handle(input) {
      const res = deps.tryHandleToolCall(toolName, input as AgentTweakableToolCall);
      if (!res.handled) {
        return { errorText: 'unhandled tool call' };
      }
      if (res.errorText) {
        return { errorText: res.errorText };
      }
      return { output: res.result ?? { ok: true } };
    },
  });
}

export function useAgentTweakableCopilotIntegration(): {
  agentTweakableToolSpecs: CopilotToolSpec[];
  getFrontendContextPromptFragmentInfo: () => PromptFragmentInfo;
} {
  const { getFrontendContext, getFrontendTools, tryHandleToolCall } = useAgentTweakableTools();

  const agentTweakableToolSpecs = useMemo(
    () =>
      AGENT_TWEAKABLE_TOOL_NAMES.map((name) =>
        makeTweakableToolSpec(name, { getFrontendTools, tryHandleToolCall })
      ),
    [getFrontendTools, tryHandleToolCall]
  );

  const getFrontendContextPromptFragmentInfo = useCallback((): PromptFragmentInfo => {
    const ctx = getFrontendContext();
    return {
      text: ctx.text,
      lineCount: ctx.lineCount,
      keys: ctx.keys,
      keySample: ctx.keySample,
    };
  }, [getFrontendContext]);

  return { agentTweakableToolSpecs, getFrontendContextPromptFragmentInfo };
}
